/**
 * <ActivityFileDropZone> — déposer un exercice à faire lire par Sam (organism).
 *
 * `tutor/students/components/ImageUploadZone.js`, en jetons. Ses deux états :
 *  - **fermé** tant que la classe et la matière ne sont pas choisies : un cadenas, « Choisissez
 *    d'abord classe et matière », et « pour analyser un fichier » dessous ; toucher la zone ne
 *    dépose rien, elle rappelle ce qui manque ;
 *  - **ouvert** : « Glisser un exercice de 4ème · Mathématiques » (le contexte remplace le
 *    « Glisser-déposer une image ou un PDF » générique), « Pas besoin du chapitre ni du type —
 *    Sam détecte tout », puis le mode d'emploi de la plateforme.
 *
 * Le séparateur « OU » qui le précède fait partie de la zone : c'est lui qui dit que le dépôt est
 * l'AUTRE façon de composer une activité, pas une étape de plus.
 *
 * `<DashedButton>` porte la surface (bordure pointillée, état de survol) ; le choix du fichier est
 * un effet injecté, comme dans `<MediaPicker>`.
 */
import React from "react"
import { Platform } from "react-native"

import { DashedButton } from "../../atoms/DashedButton"
import { Stack } from "../../atoms/containers/Stack"
import { Divider } from "../../atoms/Divider"
import { Text } from "../../atoms/typography/Text"
import { type Voice } from "../../utils/voice"

/** Les deux voix d'`ImageUploadZone.js` (`isParentMode`). */
const TEXTS: Record<Voice, { scopeFirst: string }> = {
    parent: { scopeFirst: "Choisissez d'abord classe et matière" },
    student: { scopeFirst: "Choisis d'abord classe et matière" },
}

export type ActivityFileDropZoneProps = {
    /** Ouvre le sélecteur de fichier. Effet injecté : le DS ne touche à aucun sélecteur. */
    onPickFile?: () => void
    /** Fermée tant que la classe et la matière manquent. */
    disabled?: boolean
    /** Ce qui manque, quand ce n'est pas classe et matière. */
    disabledLabel?: string
    /** « 4ème · Mathématiques » — le cadrage du dépôt. */
    contextLabel?: string | null
    /** Un fichier est en train d'être survolé (web). */
    dragActive?: boolean
    /** Le séparateur « OU » au-dessus. Défaut `true`. */
    withSeparator?: boolean
    /** À qui la zone parle. Défaut `"parent"`. */
    voice?: Voice
    testID?: string
}

export function ActivityFileDropZone(props: ActivityFileDropZoneProps) {
    const {
        onPickFile,
        disabled = false,
        disabledLabel,
        contextLabel,
        dragActive = false,
        withSeparator = true,
        voice = "parent",
        testID,
    } = props

    const label = disabled
        ? (disabledLabel ?? TEXTS[voice].scopeFirst)
        : dragActive
          ? "Déposer le fichier ici"
          : contextLabel != null && contextLabel !== ""
            ? `Glisser un exercice de ${contextLabel}`
            : "Glisser-déposer une image ou un PDF"

    return (
        <Stack gap="3" testID={testID}>
            {withSeparator && <Divider label="OU" spacing="4" />}
            <DashedButton
                label={label}
                iconName={disabled ? "lock" : "photo-video"}
                minHeight={140}
                disabled={disabled}
                highlighted={dragActive}
                onPress={onPickFile}
            />
            {disabled ? (
                <Text size="text-sm" color="text-muted" center>
                    pour analyser un fichier
                </Text>
            ) : (
                <Stack gap="1">
                    {contextLabel != null && contextLabel !== "" && !dragActive && (
                        <Text size="text-sm" color="primary" center>
                            Pas besoin du chapitre ni du type — Sam détecte tout
                        </Text>
                    )}
                    <Text size="text-sm" color="text-muted" center>
                        {Platform.OS === "web"
                            ? "ou cliquer · Ctrl+V pour coller"
                            : "Cliquer pour sélectionner un fichier"}
                    </Text>
                </Stack>
            )}
        </Stack>
    )
}
